import mitt, { Emitter } from './mitt';
import { Events } from './Events';
import Utils, { IS_BABYLON_NATIVE } from '../utils/utils';
import Bridge, { BridgeMessage } from '../bridge/bridge';

declare global {
  interface Window {
    // Entry point the native wrapper evaluates to deliver relayed messages
    // into this runtime (both the WKWebView page and JSCore).
    EJ_MESSAGE?: (message: any) => void;
  }
}

// The cross-layer event bus: UI layer (WKWebView page / plain web) <-> 3D
// layer (JSCore / same page). One API everywhere; the transport differs:
//
//   plain web       both layers share this singleton — emit dispatches
//                   directly to local handlers.
//   Babylon Native  the layers live in separate JS runtimes. emit posts a
//                   native/mitt message that the wrapper relays to the OTHER
//                   runtime, where it arrives via window.EJ_MESSAGE and is
//                   dispatched locally. (Fire-and-forget: the relay doesn't
//                   ack.) See NativeViewController in eyejack-ios-2.
//
// RULE: on Babylon Native, emit NEVER dispatches to handlers in the SAME
// runtime — an emit/on pair co-located in one runtime works on plain web
// and the polyfill but silently no-ops on native. Pair emits across
// runtimes (UI <-> 3D), or call the handler directly instead of emitting.
//
// mitt itself stays a pure emitter — all transport lives here, registered
// exactly once by this singleton.
class EventEmitter {
  private static instance: EventEmitter;
  private emitter: Emitter<Events>;
  private bridge: Bridge;

  private constructor() {
    this.emitter = mitt<Events>();
    this.bridge = new Bridge();

    // Receive events relayed from the other runtime. The wrapper delivers by
    // evaluating window.EJ_MESSAGE(message) in this runtime.
    if (typeof window !== 'undefined' && IS_BABYLON_NATIVE) {
      window.EJ_MESSAGE = (message: any) => {
        if (message?.type === 'native/mitt') {
          this.emitter.emit(message.data.type, message.data.evt);
        }
      };
    }
  }

  public static getInstance(): EventEmitter {
    if (!EventEmitter.instance) {
      EventEmitter.instance = new EventEmitter();
    }
    return EventEmitter.instance;
  }

  public on<Key extends keyof Events>(type: Key, handler: (event: Events[Key]) => void): void {
    this.emitter.on(type, handler);
  }

  public off<Key extends keyof Events>(type: Key, handler: (event: Events[Key]) => void): void {
    this.emitter.off(type, handler);
  }

  public emit<Key extends keyof Events>(type: Key, event?: Events[Key]): void {
    if (IS_BABYLON_NATIVE) {
      // Cross-runtime: this event is FOR the other layer.
      const message: BridgeMessage = {
        uuid: Utils.UUID(),
        type: 'native/mitt',
        data: {
          type: type,
          evt: event,
        },
      };
      if (!this.bridge.post(message)) {
        console.error('EventEmitter.emit: bridge post failed for', String(type));
      }
      return;
    }
    // Same-context (plain web): dispatch to local handlers.
    this.emitter.emit(type, event as Events[Key]);
  }
}

export default EventEmitter.getInstance();
